import { z } from 'zod'

import type { RequestService } from '../request'
import { themeSchema } from '@scalar-org/entities/theme'

/**
 * API for interacting with themes
 *
 * This is a wrapper around the core/themes endpoints in the core service
 */
export function themesApiFactory(requestService: RequestService) {
  /** Retrieve theme contents */
  async function get(slug: string) {
    return await requestService.request({
      url: `core/themes/${slug}`,
      method: 'get',
      schema: z.string(),
    })
  }

  /** Retrieve a list of all themes for the current team */
  async function getThemes() {
    return await requestService.request({
      url: 'core/themes',
      method: 'get',
      schema: themeSchema
        .innerType()
        .pick({
          uid: true,
          name: true,
          description: true,
          slug: true,
        })
        .array(),
    })
  }

  /** Upload a new theme */
  async function create(data: {
    name: string
    description?: string
    slug: string
    document: string
  }) {
    return await requestService.request({
      url: 'core/themes',
      method: 'post',
      data,
      schema: z.object({ uid: z.string() }),
    })
  }

  /** Update an existing theme contents */
  async function replace(slug: string, data: { document: string }) {
    return await requestService.request({
      url: `core/themes/${slug}`,
      method: 'put',
      data,
      schema: z.null(),
    })
  }

  /** Update theme metadata */
  async function update(
    slug: string,
    data: Partial<{
      name: string
      description: string
    }>,
  ) {
    return await requestService.request({
      url: `core/themes/${slug}`,
      method: 'PATCH',
      data,
      schema: z.null(),
    })
  }

  /** Delete a theme */
  async function del(slug: string) {
    return await requestService.request({
      url: `core/themes/${slug}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  return {
    get,
    getThemes,
    create,
    update,
    replace,
    delete: del,
  }
}
